import { useEffect } from 'react'
import { useSubscription } from '../context/SubscriptionContext'
import { trackSubscriptionView, trackSubscriptionPurchase } from '../lib/analytics'
import PageShell from '../components/layout/PageShell'
import GlassCard from '../components/ui/GlassCard'

const PLANS = [
  {
    id: '$rc_monthly',
    name: 'Pro Monthly',
    price: '$3.99',
    period: '/month',
    highlight: false,
    features: ['AI Financial Analysis', 'Paycheck Analysis', 'PDF Report Export', 'Priority support'],
  },
  {
    id: '$rc_annual',
    name: 'Pro Annual',
    price: '$39.99',
    period: '/year',
    highlight: true,
    badge: 'Best Value',
    features: ['Everything in Monthly', 'Save 17% vs monthly', 'AI Financial Analysis', 'Paycheck Analysis', 'PDF Report Export'],
  },
  {
    id: '$rc_lifetime',
    name: 'Pro Lifetime',
    price: '$89.99',
    period: 'one-time',
    highlight: false,
    features: ['Everything in Annual', 'Pay once, own forever', 'All future Pro features', 'Lifetime updates'],
  },
]

const FREE_FEATURES = [
  'Dashboard & Charts',
  'Upload bank statements (CSV/PDF)',
  'Transaction categorization',
  'Transfer detection',
  'All 6 scenario calculators',
  'Dark & light themes',
]

export default function Pricing() {
  const { isPro, purchasePackage } = useSubscription()

  useEffect(() => {
    trackSubscriptionView()
  }, [])

  async function handlePurchase(planId: string, period: string) {
    try {
      await purchasePackage(planId)
      trackSubscriptionPurchase('pro', period)
    } catch {
      // Purchase cancelled or failed — handled by RevenueCat
    }
  }

  return (
    <PageShell title="Pricing" subtitle="Upgrade to unlock AI-powered features">
      {isPro && (
        <div className="mb-6 p-4 rounded-xl bg-positive-soft border border-positive/20 text-center">
          <p className="text-sm text-positive font-medium">You're on the Pro plan. Thank you for your support!</p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {/* Free Tier */}
        <GlassCard className="flex flex-col">
          <h3 className="text-lg font-bold text-heading mb-1">Free</h3>
          <p className="text-2xl font-bold text-heading mb-1">$0</p>
          <p className="text-xs text-muted mb-4">forever</p>
          <ul className="space-y-2 flex-1 mb-4">
            {FREE_FEATURES.map((feature, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-body">
                <svg className="w-4 h-4 text-positive shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
                {feature}
              </li>
            ))}
          </ul>
          <button disabled className="w-full py-2.5 rounded-xl border border-card text-muted text-sm font-medium cursor-default">
            Current Plan
          </button>
        </GlassCard>

        {/* Pro Plans */}
        {PLANS.map((plan) => (
          <div
            key={plan.id}
            className={`rounded-2xl p-px ${plan.highlight ? 'bg-gradient-to-b from-cyan-400/50 to-blue-500/50' : ''}`}
          >
            <GlassCard className={`flex flex-col h-full ${plan.highlight ? '!bg-card' : ''}`}>
              <div className="flex items-center gap-2 mb-1">
                <h3 className="text-lg font-bold text-heading">{plan.name}</h3>
                {plan.badge && (
                  <span className="px-2 py-0.5 rounded-lg bg-primary-soft text-primary text-xs font-medium">
                    {plan.badge}
                  </span>
                )}
              </div>
              <p className="text-2xl font-bold text-heading mb-1">{plan.price}</p>
              <p className="text-xs text-muted mb-4">{plan.period}</p>
              <ul className="space-y-2 flex-1 mb-4">
                {plan.features.map((feature, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-body">
                    <svg className="w-4 h-4 text-primary shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                    {feature}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => handlePurchase(plan.id, plan.period)}
                disabled={isPro}
                className={`w-full py-2.5 rounded-xl text-sm font-semibold transition-colors ${
                  isPro
                    ? 'border border-card text-muted cursor-default'
                    : plan.highlight
                      ? 'bg-cyan-400 text-gray-900 hover:bg-cyan-300'
                      : 'border border-primary text-primary hover:bg-primary-soft'
                }`}
              >
                {isPro ? 'Active' : 'Upgrade'}
              </button>
            </GlassCard>
          </div>
        ))}
      </div>
    </PageShell>
  )
}
